import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Cell, Pie, PieChart } from 'recharts';
import {
  BarOrientation,
  BarThickness,
  DataResult,
  ColoredThreshold,
  WidgetSettingsDto,
} from '@thingslog/repositories';
import { Widget } from './Widget';
import { DataResultParser } from './parsing/DataResultParser';
import { DataResultParseStream } from './parsing/DataResultParsed';
import { CHART_LEGEND_ASIDE_MAX_WIDTH, ChartEmptyState, ChartWidgetFrame } from './utils';
import { WidgetSettingsFactory } from '../../factories/WidgetSettings.factory';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

interface GaugeSegment {
  color: string;
  end: number;
  label: string;
  start: number;
  value: number;
}

interface GaugeValue {
  value: number;
}

interface GaugeDialProps {
  gaugeValue: GaugeValue;
  legendAside: boolean;
  maxValue: number;
  minValue: number;
  segments: GaugeSegment[];
  showMinMaxLabels: boolean;
  showThresholdLabels: boolean;
  showValue: boolean;
  thresholds: ColoredThreshold[];
  valueRatio: number;
}

interface GaugeDialSize {
  height: number;
  width: number;
}

const GAUGE_COLORS = {
  normal: '#3B82F6',
};

const GAUGE_BAR_THICKNESS_PIXELS: Record<BarThickness, number> = {
  slim: 24,
  regular: 36,
  wide: 52,
};

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

const toNumber = (value: string | number | undefined): number | null => {
  if (value === undefined) {
    return null;
  }

  const numeric = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(numeric) ? numeric : null;
};

const formatGaugeNumber = (value: number): string => {
  return Number.isInteger(value) ? value.toString() : value.toFixed(2);
};

const toFiniteNumber = (value: number, fallback: number): number => {
  return Number.isFinite(value) ? value : fallback;
};

const resolveColor = (color: string | undefined, fallback: string): string => {
  return color && HEX_COLOR_PATTERN.test(color) ? color : fallback;
};

const formatGaugeSegmentLabel = (label: string, t: TFunction): string => {
  if (label === 'Normal') {
    return t('dashboard_gauge_normal', 'Normal');
  }

  if (label === 'Warning') {
    return t('dashboard_visualization_threshold_warning', 'Warning');
  }

  if (label === 'Critical') {
    return t('dashboard_visualization_threshold_critical', 'Critical');
  }

  return label;
};

const extractGaugeValue = (dataStreams: DataResultParseStream[]): GaugeValue | null => {
  const stream = dataStreams[dataStreams.length - 1];
  const dataPoint = stream?.data[stream.data.length - 1];
  const dataKey = stream?.visualization[stream.visualization.length - 1]?.dataKey;

  if (!dataPoint || !dataKey) {
    return null;
  }

  const value = toNumber(dataPoint[dataKey]);

  return value === null ? null : { value };
};

const createGaugeSegments = (
  minValue: number,
  maxValue: number,
  thresholds: ColoredThreshold[],
  gaugeColor: string
): GaugeSegment[] => {
  const orderedThresholds = thresholds
    .map((threshold: ColoredThreshold) => ({
      ...threshold,
      value: Number(threshold.value),
    }))
    .filter(
      (threshold: ColoredThreshold) => threshold.value > minValue && threshold.value < maxValue
    )
    .sort((a: ColoredThreshold, b: ColoredThreshold) => a.value - b.value);
  const boundaries = [
    minValue,
    ...orderedThresholds.map((threshold: ColoredThreshold) => threshold.value),
    maxValue,
  ];

  return boundaries.slice(0, -1).map((start: number, index: number): GaugeSegment => {
    const threshold = orderedThresholds[index - 1];
    const fallbackColor = threshold ? threshold.color : gaugeColor;
    const label = threshold?.label || 'Normal';

    return {
      color: resolveColor(threshold?.color, fallbackColor),
      end: boundaries[index + 1],
      label,
      start,
      value: Math.max(boundaries[index + 1] - start, 0),
    };
  });
};

const GaugeValueLabel = ({
  gaugeValue,
  showValue,
}: {
  gaugeValue: GaugeValue;
  showValue: boolean;
}): JSX.Element | null => {
  if (!showValue) {
    return null;
  }

  return (
    <div className="text-center">
      <div className="text-[28px] font-semibold leading-none text-slate-900">
        {formatGaugeNumber(gaugeValue.value)}
      </div>
    </div>
  );
};

const GaugeMinMaxLabels = ({
  maxValue,
  minValue,
  orientation = 'horizontal',
  show,
}: {
  maxValue: number;
  minValue: number;
  orientation?: BarOrientation;
  show: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation();

  if (!show) {
    return null;
  }

  if (orientation === 'vertical') {
    return (
      <div className="flex h-full flex-col justify-between py-1 text-right text-[11px] font-medium text-slate-500">
        <span>{formatGaugeNumber(maxValue)}</span>
        <span>{formatGaugeNumber(minValue)}</span>
      </div>
    );
  }

  return (
    <div className="flex justify-between text-[11px] font-medium text-slate-500">
      <span>{formatGaugeNumber(minValue)}</span>
      <span>{formatGaugeNumber(maxValue)}</span>
    </div>
  );
};

const GaugeThresholdLabels = ({
  show,
  thresholds,
  aside = false,
}: {
  show: boolean;
  thresholds: ColoredThreshold[];
  aside?: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation();

  if (!show) {
    return null;
  }

  return (
    <div
      className={
        aside
          ? 'flex max-h-full w-full flex-col gap-y-1 overflow-y-auto pl-1 text-[11px] text-slate-500'
          : 'mt-2 flex max-h-[72px] flex-wrap justify-center gap-x-3 gap-y-1 overflow-y-auto text-[11px] text-slate-500'
      }
    >
      {thresholds.map((threshold: ColoredThreshold, index: number) => {
        const value = Number(threshold.value);

        if (!Number.isFinite(value)) {
          return null;
        }

        const label = threshold.label
          ? formatGaugeSegmentLabel(threshold.label, t)
          : t('dashboard_gauge_threshold', 'Threshold');

        return (
          <span
            key={`${threshold.value}-${threshold.label}-${index}`}
            className={
              aside ? 'flex min-w-0 items-start gap-1' : 'inline-flex max-w-full items-start gap-1'
            }
          >
            <span
              className="mt-1 h-2 w-2 shrink-0 rounded-full"
              style={{
                backgroundColor: resolveColor(threshold.color, GAUGE_COLORS.normal),
              }}
            />
            <span className="min-w-0 whitespace-normal break-words">
              {label}: {formatGaugeNumber(value)}
            </span>
          </span>
        );
      })}
    </div>
  );
};

const GaugeBar = ({
  barThickness,
  maxValue,
  minValue,
  orientation,
  segments,
  valueRatio,
}: {
  barThickness: BarThickness;
  maxValue: number;
  minValue: number;
  orientation: BarOrientation;
  segments: GaugeSegment[];
  valueRatio: number;
}): JSX.Element => {
  const { t } = useTranslation();
  const range = maxValue - minValue;
  const valuePercent = clamp(valueRatio * 100, 0, 100);
  const thickness = GAUGE_BAR_THICKNESS_PIXELS[barThickness] ?? GAUGE_BAR_THICKNESS_PIXELS.regular;

  if (orientation === 'vertical') {
    return (
      <div className="relative h-full min-h-[140px] px-3" style={{ width: thickness + 20 }}>
        <div
          className="relative h-full overflow-hidden rounded-md border border-solid border-slate-200 bg-slate-100 shadow-inner"
          style={{ width: thickness }}
        >
          <div className="flex h-full w-full flex-col-reverse">
            {segments.map((segment: GaugeSegment) => {
              const height = range > 0 ? (segment.value / range) * 100 : 0;

              if (height <= 0) {
                return null;
              }

              return (
                <div
                  key={`${segment.start}-${segment.end}-${segment.color}`}
                  className="w-full"
                  style={{ height: `${height}%`, backgroundColor: segment.color }}
                  title={`${formatGaugeSegmentLabel(segment.label, t)}: ${formatGaugeNumber(
                    segment.start
                  )} - ${formatGaugeNumber(segment.end)}`}
                />
              );
            })}
          </div>
          <div
            className="absolute inset-x-0 h-[3px] rounded-full bg-slate-950 shadow"
            style={{ bottom: `${valuePercent}%`, transform: 'translateY(50%)' }}
          />
        </div>
        <div
          className="absolute right-0 h-0 w-0"
          style={{
            borderBottom: '6px solid transparent',
            borderRight: '8px solid #020617',
            borderTop: '6px solid transparent',
            bottom: `${valuePercent}%`,
            transform: 'translateY(50%)',
          }}
        />
      </div>
    );
  }

  return (
    <div className="relative w-full pt-5">
      <div
        className="relative overflow-hidden rounded-md border border-solid border-slate-200 bg-slate-100 shadow-inner"
        style={{ height: thickness }}
      >
        <div className="flex h-full w-full">
          {segments.map((segment: GaugeSegment) => {
            const width = range > 0 ? (segment.value / range) * 100 : 0;

            if (width <= 0) {
              return null;
            }

            return (
              <div
                key={`${segment.start}-${segment.end}-${segment.color}`}
                className="h-full"
                style={{ width: `${width}%`, backgroundColor: segment.color }}
                title={`${formatGaugeSegmentLabel(segment.label, t)}: ${formatGaugeNumber(
                  segment.start
                )} - ${formatGaugeNumber(segment.end)}`}
              />
            );
          })}
        </div>
        <div
          className="absolute inset-y-0 w-[3px] rounded-full bg-slate-950 shadow"
          style={{ left: `${valuePercent}%`, transform: 'translateX(-50%)' }}
        />
      </div>
      <div
        className="absolute top-1 h-0 w-0"
        style={{
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '8px solid #020617',
          left: `${valuePercent}%`,
          transform: 'translateX(-50%)',
        }}
      />
    </div>
  );
};

const GaugeDial = ({
  gaugeValue,
  legendAside,
  maxValue,
  minValue,
  segments,
  showMinMaxLabels,
  showThresholdLabels,
  showValue,
  thresholds,
  valueRatio,
}: GaugeDialProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<GaugeDialSize>({ height: 0, width: 0 });

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const updateSize = (): void => {
      const height = container.clientHeight;
      const width = container.clientWidth;
      // Bail out when the size is unchanged so the ResizeObserver callback can
      // never feed back into itself (and to avoid redundant re-renders).
      setSize((prev: GaugeDialSize) =>
        prev.height === height && prev.width === width ? prev : { height, width }
      );
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(container);

    return (): void => observer.disconnect();
  }, []);

  const centerX = size.width / 2;
  const centerY = size.height * 0.72;
  const outerRadius = Math.max(Math.min(size.width * 0.42, centerY - 8), 0);
  const innerRadius = outerRadius * 0.74;
  const needleLength = outerRadius * 0.92;
  const minMaxLabelTop = centerY + 6;
  const hasSize = size.width > 0 && size.height > 0 && outerRadius > 0;

  const asideLayout = showThresholdLabels && legendAside;

  const gaugeColumn = (
    <div className="relative flex min-h-0 flex-1 flex-col justify-center">
      <div ref={containerRef} className="relative min-h-0 flex-1">
        {hasSize && (
          <>
            <PieChart
              width={size.width}
              height={size.height}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
              <Pie
                data={segments}
                dataKey="value"
                cx={centerX}
                cy={centerY}
                startAngle={180}
                endAngle={0}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                paddingAngle={1}
                stroke="#ffffff"
                strokeWidth={2}
                isAnimationActive={false}
              >
                {segments.map((segment: GaugeSegment) => (
                  <Cell
                    key={`${segment.start}-${segment.end}-${segment.label}`}
                    fill={segment.color}
                  />
                ))}
              </Pie>
            </PieChart>
            <div
              className="pointer-events-none absolute h-[3px] origin-left rounded-full bg-slate-800 shadow-sm"
              style={{
                left: centerX,
                top: centerY,
                transform: `rotate(${180 + 180 * valueRatio}deg) translateX(-1px)`,
                width: needleLength,
              }}
            />
            <div
              className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-800 shadow-sm"
              style={{ left: centerX, top: centerY }}
            />
            {showMinMaxLabels && (
              <>
                <span
                  className="pointer-events-none absolute -translate-x-1/2 text-[11px] font-medium text-slate-500"
                  style={{ left: centerX - outerRadius, top: minMaxLabelTop }}
                >
                  {formatGaugeNumber(minValue)}
                </span>
                <span
                  className="pointer-events-none absolute -translate-x-1/2 text-[11px] font-medium text-slate-500"
                  style={{ left: centerX + outerRadius, top: minMaxLabelTop }}
                >
                  {formatGaugeNumber(maxValue)}
                </span>
              </>
            )}
          </>
        )}
        <div className="absolute inset-x-0 bottom-2">
          <GaugeValueLabel gaugeValue={gaugeValue} showValue={showValue} />
        </div>
      </div>
      {!legendAside && <GaugeThresholdLabels show={showThresholdLabels} thresholds={thresholds} />}
    </div>
  );

  return (
    <ChartWidgetFrame>
      {asideLayout ? (
        <div className="flex min-h-0 flex-1 flex-row items-stretch gap-2">
          {gaugeColumn}
          <div
            className="flex min-w-0 items-center"
            style={{ maxWidth: CHART_LEGEND_ASIDE_MAX_WIDTH }}
          >
            <GaugeThresholdLabels show={showThresholdLabels} thresholds={thresholds} aside />
          </div>
        </div>
      ) : (
        gaugeColumn
      )}
    </ChartWidgetFrame>
  );
};

export class GaugeWidget implements Widget {
  public render(data: DataResult, widgetSettings: WidgetSettingsDto): ReactNode {
    const normalizedSettings = WidgetSettingsFactory.normalize(widgetSettings);

    if (normalizedSettings.type !== 'gauge') {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_gauge_settings_missing"
          description="Gauge settings are missing."
        />
      );
    }

    const dataParser = new DataResultParser(normalizedSettings.query.type);
    const parsedData = dataParser.parse(data, normalizedSettings.type);
    const noData = parsedData.dataStreams.every(
      (stream: DataResultParseStream) => stream.data.length === 0
    );

    if (noData) {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_widget_no_value"
          description="This widget has no value to display."
        />
      );
    }

    const gaugeValue = extractGaugeValue(parsedData.dataStreams);

    if (!gaugeValue) {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_widget_no_numeric_value"
          description="This widget has no numeric value to display."
        />
      );
    }

    const rawMin = Number(normalizedSettings.visualization.minValue);
    const rawMax = Number(normalizedSettings.visualization.maxValue);
    const minValue = toFiniteNumber(rawMin, 0);
    const maxValue = Number.isFinite(rawMax) && rawMax > minValue ? rawMax : minValue + 100;
    const clampedValue = clamp(gaugeValue.value, minValue, maxValue);
    const valueRatio = (clampedValue - minValue) / (maxValue - minValue);
    const gaugeColor = resolveColor(normalizedSettings.visualization.color, GAUGE_COLORS.normal);
    const barOrientation = normalizedSettings.visualization.barOrientation;
    const barThickness = normalizedSettings.visualization.barThickness;
    const segments = createGaugeSegments(
      minValue,
      maxValue,
      normalizedSettings.visualization.thresholds,
      gaugeColor
    );
    const showLegend = normalizedSettings.visualization.showLegend;
    const legendAside = normalizedSettings.visualization.showLegendAside;

    if (normalizedSettings.visualization.visualizationType === 'bar') {
      const asideLayout = showLegend && legendAside;
      const barColumn = (
        <div className="flex min-h-0 flex-1 flex-col justify-center gap-4">
          <GaugeValueLabel
            gaugeValue={gaugeValue}
            showValue={normalizedSettings.visualization.showValue}
          />
          {barOrientation === 'vertical' ? (
            <div className="flex min-h-[140px] flex-1 items-stretch justify-center gap-2">
              <GaugeMinMaxLabels
                maxValue={maxValue}
                minValue={minValue}
                orientation={barOrientation}
                show={normalizedSettings.visualization.showMinMaxLabels}
              />
              <GaugeBar
                barThickness={barThickness}
                maxValue={maxValue}
                minValue={minValue}
                orientation={barOrientation}
                segments={segments}
                valueRatio={valueRatio}
              />
            </div>
          ) : (
            <>
              <GaugeBar
                barThickness={barThickness}
                maxValue={maxValue}
                minValue={minValue}
                orientation={barOrientation}
                segments={segments}
                valueRatio={valueRatio}
              />
              <GaugeMinMaxLabels
                maxValue={maxValue}
                minValue={minValue}
                show={normalizedSettings.visualization.showMinMaxLabels}
              />
            </>
          )}
          {!legendAside && (
            <GaugeThresholdLabels
              show={showLegend}
              thresholds={normalizedSettings.visualization.thresholds}
            />
          )}
        </div>
      );

      return (
        <ChartWidgetFrame>
          {asideLayout ? (
            <div className="flex min-h-0 flex-1 flex-row items-stretch gap-2">
              {barColumn}
              <div
                className="flex min-w-0 items-center"
                style={{ maxWidth: CHART_LEGEND_ASIDE_MAX_WIDTH }}
              >
                <GaugeThresholdLabels
                  show={showLegend}
                  thresholds={normalizedSettings.visualization.thresholds}
                  aside
                />
              </div>
            </div>
          ) : (
            barColumn
          )}
        </ChartWidgetFrame>
      );
    }

    return (
      <GaugeDial
        gaugeValue={gaugeValue}
        legendAside={legendAside}
        maxValue={maxValue}
        minValue={minValue}
        segments={segments}
        showMinMaxLabels={normalizedSettings.visualization.showMinMaxLabels}
        showThresholdLabels={showLegend}
        showValue={normalizedSettings.visualization.showValue}
        thresholds={normalizedSettings.visualization.thresholds}
        valueRatio={valueRatio}
      />
    );
  }
}
